import AdvancedSearch from '@app/components/AdvancedSearch'
import ContactProfileImage from '@app/components/ContactProfileImage'
import GroupFilterSelector from '@app/components/GroupFilterSelector'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { IContact } from '@app/models/Contact'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { Column } from 'react-virtualized'
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap'
import {
  actionCreators,
  ConnectionsScope,
  IConnectionsListState,
} from '../modules/connectionsList'

type ConnectionsListProps = IConnectionsListState &
  IGlobalConfigurationState &
  RouteComponentProps<{}> &
  typeof actionCreators

export default class ConnectionsListPage extends React.PureComponent<
  ConnectionsListProps
> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ConnectionsListProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public componentDidUpdate(prevProps: ConnectionsListProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public render() {
    return (
      <DocumentTitle title='Connections'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='mb-2 d-flex flex-row justify-content-between'>
              <h3>Connections</h3>
              <Link to='/connection/add' className='align-self-center'>
                <Button size='sm' color='primary'>
                  <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                  <span>Add Connection</span>
                </Button>
              </Link>
            </div>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for someone?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Connection Type</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.connectionType}
                    onChange={this._onChangeConnectionType}
                  >
                    {this.props.configuration.connectionTypes
                      .map((c, i) => (
                        <option key={`connectionType-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Group</Label>
                  <GroupFilterSelector
                    bsSize='sm'
                    includeAll
                    onChange={this._onChangeGroup}
                    value={this.props.group}
                  />
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort Order</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.contactSortOptions
                      .map((c, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
            </AdvancedSearch>
            <div className='mb-3 mt-5'>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    active={this.props.scope === ConnectionsScope.Mine}
                    onClick={() => this._onChangeScope(ConnectionsScope.Mine)}
                  >
                    {ConnectionsScope.Mine}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={this.props.scope === ConnectionsScope.All}
                    onClick={() => this._onChangeScope(ConnectionsScope.All)}
                  >
                    {ConnectionsScope.All}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              loadItems={this.props.loadConnections}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ cellData }) => (
                  <ContactProfileImage contact={cellData} size={30} />
                )}
                dataKey='id'
                flexGrow={0}
                flexShrink={0}
                width={30}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const contact = rowData as IContact
                  if (contact.firstName) {
                    return `${contact.firstName} ${contact.lastName}`
                  } else {
                    return contact.lastName
                  }
                }}
                dataKey='name'
                flexGrow={1}
                label='Name'
                width={200}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const contact = rowData as IContact
                  return contact.company ? contact.company.name : ''
                }}
                className='d-none d-md-block'
                dataKey='company'
                flexGrow={1}
                headerClassName='d-none d-md-block'
                label='Company'
                width={200}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const contact = rowData as IContact
                  return contact.groups.size > 0
                    ? contact.groups
                        .map((g) => (g !== undefined ? g.name : ''))
                        .join(', ')
                    : ''
                }}
                className='d-none d-xl-block'
                dataKey='groups'
                flexGrow={1}
                headerClassName='d-none d-xl-block'
                label='Groups'
                width={150}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No connections</div>
  }

  private _onRowClicked(item: IContact, index: number) {
    this.props.history.push(`/connection/${item.id}`)
  }

  private _onChangeConnectionType(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setConnectionType(event.target.value)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeGroup(newGroup: string) {
    this.props.setGroup(newGroup)
    this._reloadData()
  }

  private _onChangeScope(newScope: ConnectionsScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
